import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { botService } from '../services/bot'
import { formatPrice, formatPercentage } from '@/utils/formatters'

interface BacktestParams {
  fecha_inicio: string
  fecha_fin: string
  capital_inicial: number
  timeframe: string
}

interface BacktestResult {
  retorno_total: number
  sharpe_ratio: number
  max_drawdown: number
  win_rate: number
  total_operaciones: number
  capital_inicial: number
  capital_final: number
}

interface OptimizationParams {
  fecha_inicio: string
  fecha_fin: string
  grids_min: number
  grids_max: number
  porcentaje_rango_min: number
  porcentaje_rango_max: number
}

const BacktestingPage: React.FC = () => {
  // Estado para backtest simple
  const [backtestParams, setBacktestParams] = useState<BacktestParams>({
    fecha_inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
    capital_inicial: 10000,
    timeframe: '15m'
  })
  const [backtestResult, setBacktestResult] = useState<BacktestResult | null>(null)
  const [runningBacktest, setRunningBacktest] = useState(false)

  // Estado para optimización
  const [optimizationParams, setOptimizationParams] = useState<OptimizationParams>({
    fecha_inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
    grids_min: 5,
    grids_max: 20,
    porcentaje_rango_min: 5,
    porcentaje_rango_max: 15
  })
  const [optimizationResult, setOptimizationResult] = useState<any>(null)
  const [runningOptimization, setRunningOptimization] = useState(false)

  // Estado para vista activa
  const [activeTab, setActiveTab] = useState<'backtest' | 'optimization'>('backtest')

  const handleRunBacktest = async () => {
    setRunningBacktest(true)
    try {
      const result = await botService.runBacktest(backtestParams)
      setBacktestResult(result.metricas)
    } catch (error) {
      console.error('Error running backtest:', error)
      toast.error('Error al ejecutar el backtest')
    } finally {
      setRunningBacktest(false)
    }
  }

  const handleRunOptimization = async () => {
    setRunningOptimization(true)
    try {
      const result = await botService.optimizeParameters(optimizationParams)
      setOptimizationResult(result)
    } catch (error) {
      console.error('Error running optimization:', error)
      toast.error('Error al ejecutar la optimización')
    } finally {
      setRunningOptimization(false)
    }
  }

  const getResultColor = (value: number, type: 'positive' | 'negative') => {
    if (type === 'positive') {
      return value > 0 ? 'text-green-600' : 'text-red-600'
    } else {
      return value < 0 ? 'text-green-600' : 'text-red-600'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">📊 Backtesting y Optimización</h1>
          <p className="text-sm text-gray-600 mt-1">Prueba y optimiza tu estrategia con datos históricos</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('backtest')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'backtest'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📈 Backtest Simple
            </button>
            <button
              onClick={() => setActiveTab('optimization')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'optimization'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🎯 Optimización de Parámetros
            </button>
          </nav>
        </div>

        {/* Backtest Tab */}
        {activeTab === 'backtest' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formulario de Backtest */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Configuración del Backtest</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={backtestParams.fecha_inicio}
                    onChange={(e) => setBacktestParams({...backtestParams, fecha_inicio: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={backtestParams.fecha_fin}
                    onChange={(e) => setBacktestParams({...backtestParams, fecha_fin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Capital Inicial ($)
                  </label>
                  <input
                    type="number"
                    value={backtestParams.capital_inicial}
                    onChange={(e) => setBacktestParams({...backtestParams, capital_inicial: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="100"
                    step="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeframe
                  </label>
                  <select
                    value={backtestParams.timeframe}
                    onChange={(e) => setBacktestParams({...backtestParams, timeframe: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1m">1 minuto</option>
                    <option value="5m">5 minutos</option>
                    <option value="15m">15 minutos</option>
                    <option value="30m">30 minutos</option>
                    <option value="1h">1 hora</option>
                    <option value="4h">4 horas</option>
                    <option value="1d">1 día</option>
                  </select>
                </div>

                <button
                  onClick={handleRunBacktest}
                  disabled={runningBacktest}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 font-medium"
                >
                  {runningBacktest ? '⏳ Ejecutando Backtest...' : '▶️ Ejecutar Backtest'}
                </button>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-blue-500">ℹ️</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        ¿Qué es un Backtest?
                      </h3>
                      <p className="mt-1 text-sm text-blue-700">
                        Un backtest simula tu estrategia usando datos históricos para evaluar su rendimiento. 
                        Te ayuda a entender cómo habría funcionado tu estrategia en el pasado.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resultados del Backtest */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resultados</h3>
                
                {backtestResult ? (
                  <div className="space-y-4">
                    {/* Retorno Total */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-700">Retorno Total</p>
                          <p className={`text-3xl font-bold ${getResultColor(backtestResult.retorno_total, 'positive')}`}>
                            {backtestResult.retorno_total >= 0 ? '+' : ''}{formatPercentage(backtestResult.retorno_total, 2)}
                          </p>
                        </div>
                        <div className="text-5xl">📈</div>
                      </div>
                    </div>

                    {/* Capital */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 mb-1">Capital Inicial</p>
                        <p className="text-lg font-bold text-gray-900">{formatPrice(backtestResult.capital_inicial, 2)}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 mb-1">Capital Final</p>
                        <p className={`text-lg font-bold ${getResultColor(backtestResult.capital_final - backtestResult.capital_inicial, 'positive')}`}>
                          {formatPrice(backtestResult.capital_final, 2)}
                        </p>
                      </div>
                    </div>

                    {/* Métricas */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Sharpe Ratio</span>
                        <span className="text-lg font-bold text-purple-600">{backtestResult.sharpe_ratio.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Max Drawdown</span>
                        <span className={`text-lg font-bold ${getResultColor(backtestResult.max_drawdown, 'negative')}`}>
                          {formatPercentage(backtestResult.max_drawdown, 2)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Win Rate</span>
                        <span className="text-lg font-bold text-green-600">{formatPercentage(backtestResult.win_rate, 2)}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Total Operaciones</span>
                        <span className="text-lg font-bold text-blue-600">{backtestResult.total_operaciones}</span>
                      </div>
                    </div>

                    {/* Interpretación */}
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">💡 Interpretación</h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        <li>• <strong>Sharpe Ratio {'>'} 1:</strong> Buen rendimiento ajustado al riesgo</li>
                        <li>• <strong>Max Drawdown {'<'} 20%:</strong> Riesgo controlado</li>
                        <li>• <strong>Win Rate {'>'} 50%:</strong> Más operaciones ganadoras que perdedoras</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="text-gray-400 text-6xl mb-4">📊</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Sin resultados</h3>
                    <p className="text-gray-500">Ejecuta un backtest para ver los resultados aquí</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Optimization Tab */}
        {activeTab === 'optimization' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formulario de Optimización */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Parámetros de Optimización</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha Inicio
                    </label>
                    <input
                      type="date"
                      value={optimizationParams.fecha_inicio}
                      onChange={(e) => setOptimizationParams({...optimizationParams, fecha_inicio: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha Fin
                    </label>
                    <input
                      type="date"
                      value={optimizationParams.fecha_fin}
                      onChange={(e) => setOptimizationParams({...optimizationParams, fecha_fin: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Rango de Grids</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mínimo
                      </label>
                      <input
                        type="number"
                        value={optimizationParams.grids_min}
                        onChange={(e) => setOptimizationParams({...optimizationParams, grids_min: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Máximo
                      </label>
                      <input
                        type="number"
                        value={optimizationParams.grids_max}
                        onChange={(e) => setOptimizationParams({...optimizationParams, grids_max: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="5"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Porcentaje de Rango (%)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mínimo
                      </label>
                      <input
                        type="number"
                        value={optimizationParams.porcentaje_rango_min}
                        onChange={(e) => setOptimizationParams({...optimizationParams, porcentaje_rango_min: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        step="0.5"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Máximo
                      </label>
                      <input
                        type="number"
                        value={optimizationParams.porcentaje_rango_max}
                        onChange={(e) => setOptimizationParams({...optimizationParams, porcentaje_rango_max: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="5"
                        step="0.5"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleRunOptimization}
                  disabled={runningOptimization}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 font-medium"
                >
                  {runningOptimization ? '⏳ Optimizando...' : '🎯 Ejecutar Optimización'}
                </button>

                <div className="p-4 bg-purple-50 border border-purple-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-purple-500">ℹ️</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-purple-800">
                        ¿Qué es la Optimización?
                      </h3>
                      <p className="mt-1 text-sm text-purple-700">
                        La optimización prueba múltiples combinaciones de parámetros para encontrar 
                        la configuración que mejor rendimiento hubiera dado en el período histórico seleccionado.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resultados de Optimización */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Mejores Parámetros</h3>
                
                {optimizationResult ? (
                  <div className="space-y-4">
                    {/* Mejor combinación */}
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
                      <h4 className="text-sm font-semibold text-purple-900 mb-3">🏆 Configuración Óptima</h4>
                      <div className="space-y-2">
                        {optimizationResult.mejores_parametros && Object.entries(optimizationResult.mejores_parametros).map(([key, value]: [string, any]) => (
                          <div key={key} className="flex justify-between items-center">
                            <span className="text-sm text-purple-700 capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="text-sm font-bold text-purple-900">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Estadísticas */}
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Combinaciones Probadas</p>
                      <p className="text-2xl font-bold text-blue-600">{optimizationResult.total_combinaciones_probadas}</p>
                    </div>

                    {/* Top 5 */}
                    {optimizationResult.top_5_combinaciones && optimizationResult.top_5_combinaciones.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">📊 Top 5 Combinaciones</h4>
                        <div className="space-y-2">
                          {optimizationResult.top_5_combinaciones.map((combo: any, index: number) => (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-600">#{index + 1}</span>
                                <span className={`text-sm font-bold ${combo.retorno >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {combo.retorno >= 0 ? '+' : ''}{formatPercentage(combo.retorno, 2)}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                {Object.entries(combo).filter(([key]) => key !== 'retorno').map(([key, value]: [string, any]) => (
                                  <div key={key}>
                                    <span className="capitalize">{key.replace(/_/g, ' ')}: </span>
                                    <span className="font-medium">{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">⚠️ Importante</h4>
                      <p className="text-sm text-yellow-700">
                        Los resultados de optimización se basan en datos históricos. El rendimiento pasado 
                        no garantiza resultados futuros. Siempre prueba en modo simulación antes de usar en real.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="text-gray-400 text-6xl mb-4">🎯</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Sin resultados</h3>
                    <p className="text-gray-500">Ejecuta una optimización para ver los mejores parámetros aquí</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BacktestingPage